import { splitWords } from './wordNotes'

/**
 * The shared dictionary on the learner side (V2-w).
 *
 * One rule, three implementations — this file, the backend
 * (`src/dictionary/normalize-word.ts`) and the Android app — pinned by one
 * table, `backend-nest/docs/word-normalization-cases.json`, which
 * `words.test.js` reads. The prose is in `backend-nest/docs/api-contract.md`
 * ("V2-b — the shared dictionary"). Change the table and the contract first.
 *
 * A sentence's words are `textEn.split(/\s+/)` (`splitWords`, unchanged from
 * the word-notes feature); `index` everywhere on the wire — `wordNotes`,
 * `words` — is the position in that array.
 */

/** Every leading and trailing run of characters that are neither a letter nor a decimal digit. */
const EDGE = /^[^\p{L}\p{Nd}]+|[^\p{L}\p{Nd}]+$/gu

/** `splitWords`, under the name the contract uses. */
export const tokenize = splitWords

/**
 * The key a word is looked up by: curly apostrophes folded to `'`, edge
 * characters that are not letters or digits removed, lowercased. Characters
 * inside the token are left alone (`don't`, `well-known`, `and/or`). `''` for
 * a token with nothing left (`--`, `...`).
 */
export function normalizeWord(raw) {
  return String(raw ?? '')
    .replace(/[‘’]/g, "'")
    .replace(EDGE, '')
    .toLowerCase()
}

/** `tokenize` then `normalizeWord`, index for index. */
export function normalizedWords(textEn) {
  return tokenize(textEn).map(normalizeWord)
}

/** The published entry linked at `index`, or null — `words` is `SentenceDto.words` (V2-b). */
export function entryForWord(words, index) {
  return (words ?? []).find((word) => word.index === index)?.entry ?? null
}

/**
 * What the tap-a-word sheet shows for one word, in order of preference: the
 * shared-dictionary entry (V2), else the old admin note (kept on the wire
 * for one more release), else nothing — the sheet says so. `wordNotes` and
 * `words` are the sentence's two wire fields; either may be missing.
 */
export function wordInfo({ wordNotes, words } = {}, index) {
  const entry = entryForWord(words, index)
  if (entry) return { kind: 'entry', entry }
  const note = (wordNotes ?? []).find((item) => item.index === index)?.note
  if (note) return { kind: 'note', note }
  return { kind: 'none' }
}

/** Which words to mark as "tap me": linked ones stronger than noted ones. */
export function wordMark({ wordNotes, words } = {}, index) {
  if (entryForWord(words, index)) return 'entry'
  if ((wordNotes ?? []).some((item) => item.index === index)) return 'note'
  return null
}
